import React, { useState, useEffect } from "react";
import Icon from "components/AppIcon";
import api from "../../../lib/api";
import { useAuth } from "../../../contexts/AuthContext";
import { useBranch } from "../../../contexts/BranchContext";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function KPICard({ label, value, icon, color, sub }) {
  return (
    <div className="flex items-center gap-3 p-4 bg-[var(--color-card)] rounded-[var(--radius-sm)] border border-[var(--color-border)]">
      <div className="w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}18` }}>
        <Icon name={icon} size={18} color={color} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] truncate">{label}</p>
        <p className="text-base font-bold font-[var(--font-heading)] text-[var(--color-foreground)] truncate">{value}</p>
        {sub && <p className="text-xs text-[var(--color-muted-foreground)]">{sub}</p>}
      </div>
    </div>
  );
}

function SkeletonCard() {
  return <div className="h-20 bg-[var(--color-muted)] rounded-[var(--radius-sm)] animate-pulse" />;
}

export default function ExpenseSummaryDashboard() {
  useAuth();
  const { selectedBranchId } = useBranch();
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState({ thisMonth: 0, thisYear: 0, largestCategory: "—", avgMonthly: 0 });
  const [trendData, setTrendData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const now = new Date();
      const yearStart = `${now?.getFullYear()}-01-01`;
      const monthStart = `${now?.getFullYear()}-${String(now?.getMonth() + 1)?.padStart(2, "0")}-01`;

      const params = { from: yearStart, limit: 2000 };
      if (selectedBranchId) params.branch_id = selectedBranchId;
      const { data } = await api.get('/expenses', { params });

      const expenses = data || [];

      // This month total
      const thisMonth = expenses
        ?.filter(e => e?.expense_date >= monthStart)
        ?.reduce((s, e) => s + parseFloat(e?.amount || 0), 0);

      // This year total
      const thisYear = expenses?.reduce((s, e) => s + parseFloat(e?.amount || 0), 0);

      // Monthly trend (last 6 months)
      const trend = [];
      for (let i = 5; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const mStart = `${d?.getFullYear()}-${String(d?.getMonth() + 1)?.padStart(2, "0")}-01`;
        const mEnd = new Date(d.getFullYear(), d.getMonth() + 1, 0);
        const mEndStr = `${mEnd?.getFullYear()}-${String(mEnd?.getMonth() + 1)?.padStart(2, "0")}-${String(mEnd?.getDate())?.padStart(2, "0")}`;
        const total = expenses
          ?.filter(e => e?.expense_date >= mStart && e?.expense_date <= mEndStr)
          ?.reduce((s, e) => s + parseFloat(e?.amount || 0), 0);
        trend?.push({ month: MONTHS?.[d?.getMonth()], total: parseFloat(total?.toFixed(2)) });
      }
      setTrendData(trend);

      // Avg monthly (last 6 months)
      const avgMonthly = trend?.length > 0 ? trend?.reduce((s, t) => s + t?.total, 0) / trend?.length : 0;

      // Category breakdown
      const catMap = {};
      expenses?.forEach(e => {
        const catName = e?.category_name || "Uncategorized";
        const catColor = e?.category_color || "#6b7280";
        if (!catMap?.[catName]) catMap[catName] = { name: catName, color: catColor, total: 0 };
        catMap[catName].total += parseFloat(e?.amount || 0);
      });
      const catArr = Object.values(catMap)?.sort((a, b) => b?.total - a?.total);
      setCategoryData(catArr);

      const largestCategory = catArr?.[0]?.name || "—";

      setKpis({
        thisMonth: parseFloat(thisMonth?.toFixed(2)),
        thisYear: parseFloat(thisYear?.toFixed(2)),
        largestCategory,
        avgMonthly: parseFloat(avgMonthly?.toFixed(2)),
      });
    } catch (err) {
      console.error("Error fetching expense summary:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [selectedBranchId]);

  const fmt = v => `$${parseFloat(v || 0)?.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  const totalCat = categoryData?.reduce((s, c) => s + c?.total, 0) || 1;

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {loading ? (
          [...Array(4)]?.map((_, i) => <SkeletonCard key={i} />)
        ) : (
          <>
            <KPICard label="Total This Month" value={fmt(kpis?.thisMonth)} icon="Calendar" color="var(--color-primary)" />
            <KPICard label="Total This Year" value={fmt(kpis?.thisYear)} icon="TrendingUp" color="var(--color-accent)" />
            <KPICard label="Largest Category" value={kpis?.largestCategory} icon="PieChart" color="var(--color-warning)" />
            <KPICard label="Avg Monthly Spend" value={fmt(kpis?.avgMonthly)} icon="BarChart2" color="var(--color-secondary)" sub="last 6 months" />
          </>
        )}
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Monthly Trend Bar Chart */}
        <div className="bg-[var(--color-card)] rounded-[var(--radius-sm)] border border-[var(--color-border)] p-5">
          <h4 className="text-sm font-bold font-[var(--font-heading)] text-[var(--color-foreground)] mb-4">Monthly Expense Trend</h4>
          {loading ? (
            <div className="h-48 bg-[var(--color-muted)] rounded animate-pulse" />
          ) : trendData?.every(t => t?.total === 0) ? (
            <div className="h-48 flex flex-col items-center justify-center gap-2">
              <Icon name="BarChart2" size={28} color="var(--color-muted-foreground)" />
              <p className="text-sm text-[var(--color-muted-foreground)]">No expense data yet</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={trendData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "var(--color-muted-foreground)" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "var(--color-muted-foreground)" }} axisLine={false} tickLine={false} tickFormatter={v => `$${v >= 1000 ? `${(v / 1000)?.toFixed(1)}k` : v}`} />
                <Tooltip
                  contentStyle={{ background: "var(--color-card)", border: "1px solid var(--color-border)", borderRadius: "6px", fontSize: "12px" }}
                  formatter={v => [`$${parseFloat(v)?.toLocaleString()}`, "Expenses"]}
                />
                <Bar dataKey="total" fill="var(--color-primary)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Category Breakdown Donut */}
        <div className="bg-[var(--color-card)] rounded-[var(--radius-sm)] border border-[var(--color-border)] p-5">
          <h4 className="text-sm font-bold font-[var(--font-heading)] text-[var(--color-foreground)] mb-4">Category Breakdown</h4>
          {loading ? (
            <div className="h-48 bg-[var(--color-muted)] rounded animate-pulse" />
          ) : categoryData?.length === 0 ? (
            <div className="h-48 flex flex-col items-center justify-center gap-2">
              <Icon name="PieChart" size={28} color="var(--color-muted-foreground)" />
              <p className="text-sm text-[var(--color-muted-foreground)]">No expense data yet</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={categoryData} dataKey="total" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2}>
                  {categoryData?.map((entry, i) => (
                    <Cell key={i} fill={entry?.color || "#6b7280"} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: "var(--color-card)", border: "1px solid var(--color-border)", borderRadius: "6px", fontSize: "12px" }}
                  formatter={v => [`$${parseFloat(v)?.toLocaleString()}`, "Amount"]}
                />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: "11px" }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
      {/* Category Breakdown Table */}
      {!loading && categoryData?.length > 0 && (
        <div className="bg-[var(--color-card)] rounded-[var(--radius-sm)] border border-[var(--color-border)] overflow-hidden">
          <div className="px-5 py-3 border-b border-[var(--color-border)]">
            <h4 className="text-sm font-bold font-[var(--font-heading)] text-[var(--color-foreground)]">Spend by Category</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)] bg-[var(--color-muted)]/30">
                  <th className="px-5 py-2.5 text-left text-xs font-medium text-[var(--color-muted-foreground)]">Category</th>
                  <th className="px-5 py-2.5 text-right text-xs font-medium text-[var(--color-muted-foreground)]">Total</th>
                  <th className="px-5 py-2.5 text-right text-xs font-medium text-[var(--color-muted-foreground)]">% of Spend</th>
                  <th className="px-5 py-2.5 text-left text-xs font-medium text-[var(--color-muted-foreground)] w-40">Distribution</th>
                </tr>
              </thead>
              <tbody>
                {categoryData?.map((cat, i) => {
                  const pct = ((cat?.total / totalCat) * 100)?.toFixed(1);
                  return (
                    <tr key={i} className="border-b border-[var(--color-border)] last:border-0 hover:bg-[var(--color-muted)]/20 transition-colors">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: cat?.color }} />
                          <span className="text-sm text-[var(--color-foreground)]">{cat?.name}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-right text-sm font-medium text-[var(--color-foreground)]">{fmt(cat?.total)}</td>
                      <td className="px-5 py-3 text-right text-sm text-[var(--color-muted-foreground)]">{pct}%</td>
                      <td className="px-5 py-3">
                        <div className="w-full bg-[var(--color-muted)] rounded-full h-1.5">
                          <div className="h-1.5 rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: cat?.color }} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
